import axios from 'axios'
import { isTokenExpired } from '@/utils/auth.js'

const localStorageAccessKey = 'accessToken'

const getClient = (baseUrl = null) => {
  const options = {
    baseURL: baseUrl
  }

  const client = axios.create(options)

  // Add auth header if present and correct.
  client.interceptors.request.use(
    function (config) {
      // Don't use the Vuex store otherwise we get circular imports.
      const accessToken = localStorage.getItem(localStorageAccessKey)

      if (!!accessToken && !isTokenExpired(accessToken)) {
        config.headers.Authorization = `JWT ${accessToken}`
      }
      return config
    }, function (error) {
      return Promise.reject(error)
    }
  )

  // TODO: Add sentry/raven interceptors to client for error reporting.

  return client
}

class ApiClient {
  constructor (baseUrl = null) {
    if (baseUrl === null && process.env.VUE_APP_API_BASE_URL) {
      baseUrl = process.env.VUE_APP_API_BASE_URL
    }

    this.client = getClient(baseUrl)
  }

  paginatedFactory (url, conf) {
    const self = this
    return function paginatedFetcher (next, objList = []) {
      return self.client.get(next || url, conf)
        .then(({ data }) => {
          objList.push(...data.results)
          if (!data.next) return objList
          return paginatedFetcher(data.next, objList)
        })
        .catch(error => Promise.reject(error))
    }
  }

  get (url, conf = {}) {
    return this.client.get(url, conf)
      .then(response => Promise.resolve(response))
      .catch(error => Promise.reject(error))
  }

  getAll (url, conf = {}) {
    return this.paginatedFactory(url, conf)()
  }

  delete (url, conf = {}) {
    return this.client.delete(url, conf)
      .then(response => Promise.resolve(response))
      .catch(error => Promise.reject(error))
  }

  head (url, conf = {}) {
    return this.client.head(url, conf)
      .then(response => Promise.resolve(response))
      .catch(error => Promise.reject(error))
  }

  options (url, conf = {}) {
    return this.client.options(url, conf)
      .then(response => Promise.resolve(response))
      .catch(error => Promise.reject(error))
  }

  post (url, data = {}, conf = {}) {
    return this.client.post(url, data, conf)
      .then(response => Promise.resolve(response))
      .catch(error => Promise.reject(error))
  }

  put (url, data = {}, conf = {}) {
    return this.client.put(url, data, conf)
      .then(response => Promise.resolve(response))
      .catch(error => Promise.reject(error))
  }

  patch (url, data = {}, conf = {}) {
    return this.client.patch(url, data, conf)
      .then(response => Promise.resolve(response))
      .catch(error => Promise.reject(error))
  }
}

export { ApiClient }
